import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { slugify } from "@eaub/shared";
import type { ContentType, Entry, FieldDef } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { BooksInProducts } from "../components/BooksInProducts";
import { ErrorBanner } from "../components/ErrorBanner";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Modal } from "../components/Modal";
import { StatusBadge, UnpublishedChangesBadge } from "../components/Badge";
import { FieldInput } from "../components/fields/FieldInput";
import { asString, asStringArray } from "../lib/values";

interface LoadResult {
  type: ContentType;
  entry: Entry | null;
}

function hasValue(field: FieldDef, value: unknown): boolean {
  switch (field.kind) {
    case "text":
    case "richtext":
    case "date":
      return typeof value === "string" && value.trim().length > 0;
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    case "boolean":
      return true; // a boolean always has a value
    case "media":
    case "reference":
      return field.config.multiple
        ? asStringArray(value).length > 0
        : asString(value).length > 0;
    default:
      return value !== undefined && value !== null;
  }
}

export function EntryEditor() {
  const api = useApi();
  const navigate = useNavigate();
  const params = useParams<{ id?: string; typeId?: string }>();
  const entryId = params.id;
  const routeTypeId = params.typeId ?? "";

  const load = useCallback(async (): Promise<LoadResult> => {
    if (entryId) {
      const entry = await api.getEntry(entryId);
      const type = await api.getContentType(entry.typeId);
      return { type, entry };
    }
    const type = await api.getContentType(routeTypeId);
    return { type, entry: null };
  }, [api, entryId, routeTypeId]);

  const { data, loading, error, reload } = useAsync<LoadResult>(load, [load]);

  const [entry, setEntry] = useState<Entry | null>(null);
  const [type, setType] = useState<ContentType | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [showErrors, setShowErrors] = useState(false);
  const [toUnpublish, setToUnpublish] = useState(false);

  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | undefined>(undefined);

  // Hydrate local editable state once loaded.
  useEffect(() => {
    if (!data || hydrated) return;
    setType(data.type);
    setEntry(data.entry);
    setFormData(data.entry ? { ...data.entry.data } : {});
    setSlug(data.entry ? data.entry.slug : "");
    setSlugTouched(Boolean(data.entry));
    setHydrated(true);
  }, [data, hydrated]);

  // Auto-suggest slug from the title field value for new entries.
  useEffect(() => {
    if (!type || entry || slugTouched) return;
    const titleValue = formData[type.titleFieldKey];
    if (typeof titleValue === "string" && titleValue.length > 0) {
      setSlug(slugify(titleValue));
    }
  }, [formData, type, entry, slugTouched]);

  const errors = useMemo<Record<string, string>>(() => {
    if (!type) return {};
    const out: Record<string, string> = {};
    for (const field of type.fields) {
      if (field.required && !hasValue(field, formData[field.key])) {
        out[field.key] = `${field.label} is required`;
      }
    }
    return out;
  }, [type, formData]);

  const setFieldValue = (key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const buildInput = () => ({
    slug: slug.trim() ? slug.trim() : undefined,
    data: formData,
  });

  const save = async (): Promise<Entry | null> => {
    setShowErrors(true);
    setActionError(undefined);
    if (Object.keys(errors).length > 0) return null;

    setSaving(true);
    try {
      if (entry) {
        const updated = await api.updateEntry(entry.id, buildInput());
        setEntry(updated);
        setFormData({ ...updated.data });
        setSlug(updated.slug);
        setDirty(false);
        return updated;
      }
      const created = await api.createEntry(routeTypeId, buildInput());
      setDirty(false);
      navigate(`/entries/${created.id}/edit`, { replace: true });
      return created;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Save failed.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!entry) return;
    setActing(true);
    setActionError(undefined);
    try {
      const updated = await api.publishEntry(entry.id);
      setEntry(updated);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Publish failed.");
    } finally {
      setActing(false);
    }
  };

  const confirmUnpublish = async () => {
    if (!entry) return;
    setActing(true);
    setActionError(undefined);
    try {
      const updated = await api.unpublishEntry(entry.id);
      setEntry(updated);
      setToUnpublish(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Unpublish failed.");
    } finally {
      setActing(false);
    }
  };

  const openPreview = async () => {
    if (!entry) return;
    setPreviewLoading(true);
    setPreviewError(undefined);
    setPreviewHtml(null);
    try {
      const html = await api.previewEntry(entry.id);
      setPreviewHtml(html);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Preview failed.");
      setPreviewHtml("");
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) return <Spinner label="Loading…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;
  if (!type) return null;
  if (type.slug === "books") return <div className="page"><h1 className="page__title">Books</h1><BooksInProducts /></div>;

  const canPreview = Boolean(entry) && !dirty;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">
            {entry ? "Edit entry" : "New entry"} — {type.name}
          </h1>
          <p className="page__subtitle page__subtitle--badges">
            {entry ? (
              <>
                <StatusBadge status={entry.status} />
                {entry.hasUnpublishedChanges ? <UnpublishedChangesBadge /> : null}
                <span className="muted">v{entry.version}</span>
              </>
            ) : (
              <span className="muted">Not saved yet</span>
            )}
          </p>
        </div>
        <div className="page__actions">
          <Link to={`/content-types/${type.id}/entries`} className="btn btn--ghost btn--sm">
            Back to entries
          </Link>
        </div>
      </div>

      {actionError ? <ErrorBanner title="Action failed" message={actionError} /> : null}
      {showErrors && Object.keys(errors).length > 0 ? (
        <ErrorBanner
          title="Please complete required fields"
          message="Some required fields are missing values."
        />
      ) : null}

      <form
        className="entry-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <section className="panel">
          <div className="form-field">
            <label className="form-field__label" htmlFor="entry-slug">
              Slug <span className="muted">(optional — generated if blank)</span>
            </label>
            <input
              id="entry-slug"
              className="input"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
            />
          </div>

          {type.fields.map((field) => (
            <div className="form-field" key={field.key}>
              <label className="form-field__label" htmlFor={`field-${field.key}`}>
                {field.label}
                {field.required ? <span className="required-mark"> *</span> : null}
                {field.kind === "richtext" ? (
                  <span className="muted"> (HTML/Markdown)</span>
                ) : null}
              </label>
              <FieldInput
                field={field}
                value={formData[field.key]}
                onChange={(value) => setFieldValue(field.key, value)}
              />
              {showErrors && errors[field.key] ? (
                <span className="form-field__error">{errors[field.key]}</span>
              ) : null}
            </div>
          ))}
        </section>

        <div className="form-actions form-actions--split">
          <div className="form-actions__group">
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
            {entry && entry.status !== "published" ? (
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => void publish()}
                disabled={acting}
              >
                {acting ? "Working…" : "Publish"}
              </button>
            ) : null}
            {entry && entry.status === "published" ? (
              <>
                {entry.hasUnpublishedChanges ? (
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => void publish()}
                    disabled={acting}
                  >
                    {acting ? "Working…" : "Publish changes"}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setToUnpublish(true)}
                  disabled={acting}
                >
                  Unpublish
                </button>
              </>
            ) : null}
          </div>
          <div className="form-actions__group">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => void openPreview()}
              disabled={!canPreview || previewLoading}
              title={
                !entry
                  ? "Save the entry before previewing"
                  : dirty
                    ? "Save your changes to preview the latest version"
                    : undefined
              }
            >
              {previewLoading ? "Loading…" : "Preview"}
            </button>
          </div>
        </div>
        {entry && dirty ? (
          <p className="muted form-hint">You have unsaved changes. Save to preview or publish them.</p>
        ) : null}
      </form>

      {previewHtml !== null ? (
        <Modal title="Preview" wide onClose={() => setPreviewHtml(null)}>
          {previewError ? (
            <ErrorBanner title="Preview failed" message={previewError} />
          ) : (
            <iframe
              className="preview-frame"
              title="Entry preview"
              srcDoc={previewHtml}
              sandbox="allow-same-origin"
            />
          )}
        </Modal>
      ) : null}

      {toUnpublish ? (
        <ConfirmDialog
          title="Unpublish entry"
          message="Unpublish this entry? It will no longer appear on the published site until re-published."
          confirmLabel="Unpublish"
          danger
          busy={acting}
          onConfirm={() => void confirmUnpublish()}
          onCancel={() => setToUnpublish(false)}
        />
      ) : null}
    </div>
  );
}
